import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Shield, AirplaneTilt, Sparkle, ArrowRight, Plus, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useBrand } from "../../context/BrandContext";
import Logo from "../../components/Logo";

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtRange(s, e) {
  const fmt = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  return `${fmt(s)} → ${fmt(e)}`;
}

const STEPS = ["welcome", "direct_debit", "holiday", "done"];

export default function ClientWelcome() {
  const { user, refresh } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const step = STEPS[stepIdx];

  // If the user has already completed onboarding, kick them back to the dashboard
  // so the wizard isn't reachable by URL once finished.
  useEffect(() => {
    if (user && user.role === "client" && user.onboarded_at) {
      navigate("/client", { replace: true });
    }
  }, [user, navigate]);

  // Direct debit
  const [pmStatus, setPmStatus] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [pmStarting, setPmStarting] = useState(false);

  // Holiday
  const [awayRows, setAwayRows] = useState([]);
  const [showAwayForm, setShowAwayForm] = useState(false);

  // Final
  const [finishing, setFinishing] = useState(false);

  // Load payment status on mount to skip step 2 if already active
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/clients/me/payment-method");
        setPmStatus(data);
      } catch (_) { /* noop */ }
    })();
    (async () => {
      try {
        const { data } = await api.get("/clients/me/away");
        setAwayRows(data.periods || []);
      } catch (_) { /* noop */ }
    })();
  }, []);

  const ddActive = pmStatus?.has_method;

  const next = () => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  const back = () => setStepIdx((i) => Math.max(0, i - 1));

  const startStripeSetup = async () => {
    setPmStarting(true);
    try {
      const { data } = await api.post("/clients/me/payment-method/setup-intent");
      if (!data?.publishable_key) {
        toast.error("Your coach hasn't configured Stripe yet — message them to enable it.");
        return;
      }
      setClientSecret(data.client_secret);
      setStripePromise(loadStripe(data.publishable_key));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to start Stripe setup");
    } finally {
      setPmStarting(false);
    }
  };

  const onDdSaved = async () => {
    setClientSecret(null);
    try {
      const { data } = await api.get("/clients/me/payment-method");
      setPmStatus(data);
    } catch (_) { /* noop */ }
    toast.success("Direct debit active — you're set.");
    next();
  };

  const finish = async () => {
    setFinishing(true);
    try {
      await api.post("/clients/me/onboarded");
      if (refresh) await refresh();
      toast.success("Welcome aboard");
      navigate("/client", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't finish onboarding");
      setFinishing(false);
    }
  };

  const skipHoliday = () => next();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="client-welcome">
      <header className="border-b border-white/10">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          {brand.brand_logo_b64 ? (
            <img src={brand.brand_logo_b64} alt="" className="h-7 w-7 object-contain" />
          ) : (
            <Logo size={28} />
          )}
          <div>
            <div className="font-display text-xl leading-none uppercase">
              {brand.brand_name || "TRAINA"}
            </div>
            <div className="tracker text-zinc-500 mt-1">[ Onboarding ]</div>
          </div>
        </div>
      </header>

      <Stepper stepIdx={stepIdx} />

      <main className="max-w-[760px] mx-auto px-4 sm:px-6 py-10">
        {step === "welcome" && (
          <StepWelcome name={user?.name} brand={brand} onNext={next} />
        )}
        {step === "direct_debit" && (
          <StepDirectDebit
            ddActive={ddActive}
            clientSecret={clientSecret}
            stripePromise={stripePromise}
            pmStarting={pmStarting}
            pmBrand={pmStatus?.brand}
            pmLast4={pmStatus?.last4}
            onStartSetup={startStripeSetup}
            onSaved={onDdSaved}
            onCancelSetup={() => setClientSecret(null)}
            onNext={next}
            onBack={back}
          />
        )}
        {step === "holiday" && (
          <StepHoliday
            rows={awayRows}
            onAdd={() => setShowAwayForm(true)}
            onRemove={async (id) => {
              if (!window.confirm("Remove this holiday?")) return;
              try {
                await api.delete(`/clients/me/away/${id}`);
                toast.success("Removed");
                const { data } = await api.get("/clients/me/away");
                setAwayRows(data.periods || []);
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
              }
            }}
            onSkip={skipHoliday}
            onNext={next}
            onBack={back}
          />
        )}
        {step === "done" && (
          <StepDone
            name={user?.name}
            finishing={finishing}
            onFinish={finish}
          />
        )}
      </main>

      {showAwayForm && (
        <AwayFormInline
          onClose={() => setShowAwayForm(false)}
          onSaved={async () => {
            setShowAwayForm(false);
            try {
              const { data } = await api.get("/clients/me/away");
              setAwayRows(data.periods || []);
            } catch (_) { /* noop */ }
            toast.success("Holiday logged");
          }}
        />
      )}
    </div>
  );
}

function Stepper({ stepIdx }) {
  const labels = ["Welcome", "Direct debit", "Holiday", "All set"];
  return (
    <div className="border-b border-white/10 bg-[#0A0A0A]/90">
      <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-4 flex items-center gap-2 sm:gap-3 overflow-x-auto" data-testid="welcome-stepper">
        {labels.map((label, i) => {
          const done = i < stepIdx;
          const active = i === stepIdx;
          return (
            <div key={label} className="flex items-center gap-2 sm:gap-3 shrink-0">
              <div
                className={`tracker text-[10px] px-2 py-1 border flex items-center gap-1.5 ${
                  done ? "border-emerald-500/50 text-emerald-400" :
                  active ? "border-brand bg-brand/15 text-brand-light" :
                  "border-white/10 text-zinc-600"
                }`}
                data-testid={`step-pill-${i}`}
              >
                {done ? <CheckCircle size={11} weight="bold" /> : <span className="font-mono">{String(i + 1).padStart(2, "0")}</span>}
                {label}
              </div>
              {i < labels.length - 1 && <span className="text-zinc-700">·</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StepWelcome({ name, brand, onNext }) {
  const coach = brand.trainer_name || "Your coach";
  return (
    <div className="space-y-6" data-testid="step-welcome">
      <div>
        <Sparkle size={40} weight="duotone" className="text-brand-light" />
      </div>
      <h1 className="font-display text-4xl sm:text-5xl">Welcome, {name?.split(" ")[0] || "there"}.</h1>
      <p className="text-zinc-400 text-lg max-w-xl">
        {coach} uses {brand.brand_name || "Traina"} to handle sessions, payments, and time away in one place.
        Two quick setup steps and you're done — under a minute.
      </p>
      <ul className="space-y-3 text-sm">
        <li className="flex gap-3"><Shield size={20} weight="duotone" className="text-brand-light" /> <span><strong>Direct debit</strong> — your monthly training is auto-paid on the 1st, no chasing.</span></li>
        <li className="flex gap-3"><AirplaneTilt size={20} weight="duotone" className="text-brand-light" /> <span><strong>Holiday windows</strong> — block out travel up front so nothing gets booked while you're away.</span></li>
      </ul>
      <div className="flex justify-end pt-4">
        <button onClick={onNext} className="btn-primary" data-testid="welcome-start">
          Let's go <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function StepDirectDebit({ ddActive, clientSecret, stripePromise, pmStarting, pmBrand, pmLast4, onStartSetup, onSaved, onCancelSetup, onNext, onBack }) {
  return (
    <div className="space-y-6" data-testid="step-direct-debit">
      <div>
        <div className="tracker text-brand mb-3">[ Step 2 ]</div>
        <h1 className="font-display text-3xl sm:text-4xl">Direct debit.</h1>
        <p className="text-zinc-400 mt-2 max-w-xl">
          Add a card so your monthly session-plan fee is auto-charged. Stored securely by Stripe — your coach never sees the full card number.
        </p>
      </div>

      {ddActive ? (
        <div className="card-flat p-6 border-emerald-500/40 flex items-start gap-4" data-testid="dd-active-card">
          <CheckCircle size={28} weight="duotone" className="text-emerald-400 mt-1" />
          <div>
            <div className="tracker text-emerald-400 mb-1">DIRECT DEBIT ACTIVE</div>
            <div className="font-mono text-lg">
              {(pmBrand || "card").toUpperCase()} •••• {pmLast4 || "----"}
            </div>
            <div className="text-xs text-zinc-500 mt-1">You're good. Move on to the next step.</div>
          </div>
        </div>
      ) : clientSecret && stripePromise ? (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "night" } }}>
          <InlineSetupForm onSuccess={onSaved} onCancel={onCancelSetup} />
        </Elements>
      ) : (
        <div className="card-flat p-8 text-center" data-testid="dd-empty-card">
          <Shield size={36} weight="duotone" className="text-brand-light mx-auto mb-4" />
          <p className="text-zinc-300 mb-1">No card on file yet.</p>
          <p className="text-zinc-500 text-sm mb-5">Hooks up Stripe in a popup-free flow on this page.</p>
          <button onClick={onStartSetup} className="btn-primary" disabled={pmStarting} data-testid="dd-start-setup">
            {pmStarting ? "Loading…" : (<><Shield size={14} weight="bold" /> Set up direct debit</>)}
          </button>
          <p className="text-[10px] text-zinc-600 mt-3 flex items-center justify-center gap-1.5">
            <Warning size={11} weight="bold" /> Card details are stored by Stripe, not by your coach.
          </p>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <button onClick={onBack} className="btn-ghost" data-testid="dd-back">Back</button>
        <button
          onClick={onNext}
          disabled={!ddActive}
          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="dd-next"
          title={ddActive ? "" : "Save a card to continue"}
        >
          Continue <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function InlineSetupForm({ onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (error) {
      toast.error(error.message || "Card setup failed");
      setSubmitting(false);
      return;
    }
    if (setupIntent?.payment_method) {
      try {
        await api.post("/clients/me/payment-method/save", { payment_method_id: setupIntent.payment_method });
        onSuccess();
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save card");
        setSubmitting(false);
      }
    } else {
      toast.error("Stripe didn't return a payment method");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-flat p-6" data-testid="dd-inline-form">
      <div className="mb-4"><PaymentElement /></div>
      <div className="flex justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-ghost" disabled={submitting} data-testid="dd-inline-cancel">
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={!stripe || submitting} data-testid="dd-inline-save">
          {submitting ? "Saving…" : "Save card"}
        </button>
      </div>
    </form>
  );
}

function StepHoliday({ rows, onAdd, onRemove, onSkip, onNext, onBack }) {
  const hasAny = rows.length > 0;
  return (
    <div className="space-y-6" data-testid="step-holiday">
      <div>
        <div className="tracker text-brand mb-3">[ Step 3 ]</div>
        <h1 className="font-display text-3xl sm:text-4xl">Any holidays planned?</h1>
        <p className="text-zinc-400 mt-2 max-w-xl">
          Add any upcoming travel or breaks so your coach doesn't book you in. You can always add or remove these later — totally optional.
        </p>
      </div>

      {!hasAny ? (
        <div className="card-flat p-8 text-center" data-testid="holiday-empty">
          <AirplaneTilt size={32} weight="duotone" className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm mb-4">Nothing logged yet.</p>
          <button onClick={onAdd} className="btn-primary" data-testid="holiday-add-first">
            <Plus size={14} weight="bold" /> Add a holiday
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="holiday-list">
          {rows.map((p) => (
            <div key={p.id} className="card-flat p-4 flex items-start justify-between gap-3" data-testid={`holiday-row-${p.id}`}>
              <div>
                <div className="font-mono text-base">{fmtRange(p.start_date, p.end_date)}</div>
                {p.reason && <div className="text-sm text-zinc-400 mt-1">{p.reason}</div>}
              </div>
              <button onClick={() => onRemove(p.id)} className="btn-ghost !py-1.5 !px-3 text-red-400" data-testid={`holiday-remove-${p.id}`}>
                <Trash size={12} weight="bold" /> Remove
              </button>
            </div>
          ))}
          <button onClick={onAdd} className="btn-ghost w-full justify-center" data-testid="holiday-add-more">
            <Plus size={12} weight="bold" /> Add another
          </button>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <button onClick={onBack} className="btn-ghost" data-testid="holiday-back">Back</button>
        <div className="flex gap-3">
          {!hasAny && (
            <button onClick={onSkip} className="btn-ghost" data-testid="holiday-skip">Skip</button>
          )}
          <button onClick={onNext} className="btn-primary" data-testid="holiday-next">
            Continue <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}

function StepDone({ name, finishing, onFinish }) {
  return (
    <div className="space-y-6 text-center max-w-xl mx-auto" data-testid="step-done">
      <CheckCircle size={56} weight="duotone" className="text-emerald-400 mx-auto" />
      <h1 className="font-display text-4xl sm:text-5xl">All set, {name?.split(" ")[0] || "you"}.</h1>
      <p className="text-zinc-400 text-lg">
        Your direct debit is live and your coach has been notified about your time away.
        From here on out, just show up — we'll handle the rest.
      </p>
      <button onClick={onFinish} disabled={finishing} className="btn-primary" data-testid="welcome-finish">
        {finishing ? "Saving…" : "Go to dashboard"} <ArrowRight size={14} weight="bold" />
      </button>
    </div>
  );
}

function AwayFormInline({ onClose, onSaved }) {
  const [form, setForm] = useState({
    start_date: todayPlus(1),
    end_date: todayPlus(7),
    reason: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.end_date < form.start_date) {
      toast.error("End date must be on or after start date");
      return;
    }
    setSaving(true);
    try {
      await api.post("/clients/me/away", form);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="card-flat p-8 w-full max-w-lg my-10" data-testid="welcome-away-form">
        <div className="tracker text-brand mb-2">[ Log holiday ]</div>
        <h2 className="font-display text-3xl mb-6">When are you away?</h2>

        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <label className="block">
            <div className="tracker text-zinc-500 text-[10px] mb-1">FROM</div>
            <input
              type="date"
              required
              value={form.start_date}
              min={todayPlus(0)}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="w-full px-4 py-3 border bg-[#0A0A0A]"
              data-testid="welcome-away-start"
            />
          </label>
          <label className="block">
            <div className="tracker text-zinc-500 text-[10px] mb-1">TO (inclusive)</div>
            <input
              type="date"
              required
              value={form.end_date}
              min={form.start_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="w-full px-4 py-3 border bg-[#0A0A0A]"
              data-testid="welcome-away-end"
            />
          </label>
        </div>

        <label className="block mb-6">
          <div className="tracker text-zinc-500 text-[10px] mb-1">REASON (optional)</div>
          <input
            type="text"
            placeholder="Holiday, travel, illness, family…"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            className="w-full px-4 py-3 border bg-[#0A0A0A]"
            data-testid="welcome-away-reason"
          />
        </label>

        <div className="flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="btn-ghost" disabled={saving} data-testid="welcome-away-cancel">Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving} data-testid="welcome-away-save">
            {saving ? "Saving…" : "Log holiday"}
          </button>
        </div>
      </form>
    </div>
  );
}
